import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Mail, Lock, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function AdminResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();

  const mode = token ? "confirm" : "request";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { setDone(false); }, [mode]);

  const requestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/password-reset/request", { email });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al enviar");
    } finally { setLoading(false); }
  };

  const confirmReset = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Mínimo 8 caracteres"); return; }
    if (password !== confirm) { toast.error("Las contraseñas no coinciden"); return; }
    setLoading(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: password });
      toast.success("Contraseña actualizada — ya puedes ingresar");
      setTimeout(() => nav("/admin/login", { replace: true }), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Token inválido o expirado");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0908] grain flex items-center justify-center px-5 relative">
      <div className="orb-gold w-[500px] h-[500px] left-[-140px] top-[-100px]" />
      <div className="orb-gold w-[500px] h-[500px] right-[-140px] bottom-[-100px] opacity-70" />

      <div className="max-w-md w-full relative">
        <a href="/" className="flex items-center gap-3 justify-center mb-10">
          <img src="/logo-lineas-doradas.webp" alt="Líneas Doradas" className="h-14 w-14 object-contain" />
          <div>
            <div className="font-display text-xl text-[#F5EFE0]">Líneas Doradas</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Recuperar contraseña</div>
          </div>
        </a>

        {mode === "request" && !done && (
          <form onSubmit={requestReset} data-testid="reset-request-form" className="card-lux p-8 space-y-5">
            <h1 className="font-display text-3xl text-[#F5EFE0]">¿Olvidaste tu contraseña?</h1>
            <p className="text-sm text-[#F5EFE0]/55 -mt-3">Ingresa tu email y te enviamos un enlace para elegir una nueva.</p>
            <div>
              <label className="text-xs text-[#F5EFE0]/60 mb-1.5 block">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
                <input data-testid="reset-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                       className="w-full bg-[#171412] border border-[#3A3229] rounded-lg pl-10 pr-4 py-3 text-[#F5EFE0] placeholder-[#F5EFE0]/40 focus:border-[#D4AF37] outline-none"
                       placeholder="lineasdoradas@gmail.com" />
              </div>
            </div>
            <button data-testid="reset-request-submit" type="submit" disabled={loading} className="btn-gold w-full py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-70">
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? "Enviando…" : "Enviar enlace"}
            </button>
          </form>
        )}

        {mode === "request" && done && (
          <div className="card-lux p-8 text-center" data-testid="reset-request-done">
            <CheckCircle2 size={44} className="text-[#E8C87A] mx-auto mb-4" />
            <h1 className="font-display text-2xl text-[#F5EFE0] mb-2">Revisa tu bandeja</h1>
            <p className="text-sm text-[#F5EFE0]/60">
              Si el email existe en el sistema, acabamos de enviarte un enlace para restablecer tu contraseña. Expira en 1 hora.
            </p>
          </div>
        )}

        {mode === "confirm" && (
          <form onSubmit={confirmReset} data-testid="reset-confirm-form" className="card-lux p-8 space-y-5">
            <h1 className="font-display text-3xl text-[#F5EFE0]">Nueva contraseña</h1>
            <p className="text-sm text-[#F5EFE0]/55 -mt-3">Elige una contraseña de al menos 8 caracteres.</p>
            <div>
              <label className="text-xs text-[#F5EFE0]/60 mb-1.5 block">Nueva contraseña</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
                <input data-testid="reset-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                       className="w-full bg-[#171412] border border-[#3A3229] rounded-lg pl-10 pr-4 py-3 text-[#F5EFE0] focus:border-[#D4AF37] outline-none"
                       placeholder="••••••••" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#F5EFE0]/60 mb-1.5 block">Confirmar contraseña</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
                <input data-testid="reset-confirm" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                       className="w-full bg-[#171412] border border-[#3A3229] rounded-lg pl-10 pr-4 py-3 text-[#F5EFE0] focus:border-[#D4AF37] outline-none"
                       placeholder="••••••••" />
              </div>
            </div>
            <button data-testid="reset-confirm-submit" type="submit" disabled={loading} className="btn-gold w-full py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-70">
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? "Guardando…" : "Guardar contraseña"}
            </button>
          </form>
        )}

        <div className="text-center mt-6">
          <a href="/admin/login" className="text-xs text-[#F5EFE0]/60 hover:text-[#E8C87A] inline-flex items-center gap-1">
            <ArrowLeft size={12} /> Volver a iniciar sesión
          </a>
        </div>
      </div>
    </div>
  );
}
